import { dexp } from "./dmath";
import { hashString } from "./rng";
import { deskCount } from "./layout";
import { C, roles, staffNames, STATS, traits as traitList, type RoomType, type StatId, type Trait, gardenPlants, staffPerks } from "@bd/content";
import { priceIndex, yearOf } from "./calendar";
import type { Rng } from "./rng";
import type { Candidate, Company, GameState, PhaseId, Room, Staff } from "./state";
import { clamp, newId } from "./util";
import { perkFx } from "./perks";

export const PHASE_STAT: Record<PhaseId, StatId> = { prototype: "creativity", playtest: "systems", art: "art", rulebook: "writing" };

/** Roles that do each phase well (1.0); everyone else helps at half speed. */
const PHASE_ROLES: Record<PhaseId, string[]> = {
  prototype: ["designer", "developer"],
  playtest: ["developer", "playtest_lead", "designer"],
  art: ["artist", "sculptor"],
  rulebook: ["writer", "designer"],
};

export function traitFx(s: Staff, key: string): number {
  let v = 0;
  for (const id of s.traits) {
    const t = C.trait[id];
    const x = t?.fx[key];
    if (typeof x === "number") v += x;
  }
  // chosen strengths work like traits
  if (s.perks?.length) for (const id of s.perks) {
    const x = STAFF_PERK[id]?.fx[key];
    if (typeof x === "number") v += x;
  }
  return v;
}

const STAFF_PERK: Record<string, { id: string; name: string; desc: string; fx: Record<string, number>; level: number }> = Object.fromEntries(
  staffPerks.flatMap((t) => t.options.map((o) => [o.id, { ...o, level: t.level }])),
);

/** The strength choice waiting for this person, if they've reached a level and not picked yet. */
export function perkChoice(s: Staff): (typeof staffPerks)[number] | null {
  for (const t of staffPerks) {
    if (s.level < t.level) break;
    if (!t.options.some((o) => s.perks?.includes(o.id))) return t;
  }
  return null;
}

export function staffPerkName(id: string): string {
  return STAFF_PERK[id]?.name ?? id;
}

/** Nested trait bonus, e.g. traitBonus(s, "theme", "pets_vets"). */
export function traitBonus(s: Staff, group: "theme" | "mechanic" | "audience", id: string): number {
  let v = 0;
  for (const t of s.traits) v += C.trait[t]?.fx[group]?.[id] ?? 0;
  return v;
}

export function hasTrait(s: Staff, id: string): boolean {
  return s.traits.includes(id);
}

// ------------------------------------------------------------------ people

function pickTraits(r: Rng, year: number): string[] {
  const pool = traitList.filter((t) => !t.year || t.year <= year);
  const a = r.pick(pool);
  let b: Trait = r.pick(pool);
  for (let i = 0; i < 10 && (b.id === a.id || a.clash?.includes(b.id) || b.clash?.includes(a.id)); i++) b = r.pick(pool);
  return b.id === a.id ? [a.id] : [a.id, b.id];
}

export function makeStaff(state: GameState, role: string, tier: number, r: Rng): Staff {
  const roleDef = C.role[role];
  const stats = {} as Record<StatId, number>;
  for (const k of STATS) stats[k] = Math.round(clamp(r.normal(22 + tier * 6, 9), 5, 95));
  stats[roleDef.main] = Math.round(clamp(r.normal(34 + tier * 11, 8), 10, 98));
  if (role === "playtest_lead") stats.charisma = Math.round(clamp(stats.charisma + 8, 5, 95));
  const ts = pickTraits(r, yearOf(state.week));
  if (ts.includes("secret_genius")) stats[roleDef.main] = Math.min(99, stats[roleDef.main] + 15);
  const name = `${r.pick(staffNames.first)} ${r.pick(staffNames.last)}`;
  const main = stats[roleDef.main];
  let salary = roleDef.salary * (0.55 + main / 70) * priceIndex(state.week);
  if (ts.includes("frugal")) salary *= 0.85;
  return {
    id: newId(state, "s"), name, role, stats, traits: ts, revealed: ts.filter((t) => !C.trait[t].hidden), level: 1 + Math.floor(tier / 2), xp: 0,
    salary: Math.round(salary), morale: 70, burnout: 0, hiredWeek: state.week, look: r.int(0, 63), ageAtHire: r.int(21, 38) + tier,
  };
}

/** How old someone is now (the founder starts at 30). */
export function ageOf(s: Staff, week: number): number {
  const at = s.ageAtHire ?? (s.founder ? 30 : 22 + (hashString(`${s.id}:age`) % 24));
  return Math.floor(at + (week - s.hiredWeek) / 52);
}

/** The age someone retires at: 62 to 68 (the founder never does: it's your company). */
export function retireAge(s: Staff): number {
  return 62 + (hashString(`${s.id}:retire`) % 7);
}

export function makeFounder(state: GameState, r: Rng): Staff {
  const s = makeStaff(state, "designer", 1, r);
  s.name = "You";
  s.ageAtHire = 30;
  s.morale = 64;
  s.founder = true;
  s.salary = 0;
  s.stats = { creativity: 48, systems: 38, art: 28, writing: 34, craft: 24, charisma: 42 };
  s.traits = ["team_player"];
  s.revealed = ["team_player"];
  s.level = 1;
  s.morale = 80;
  return s;
}

/** Fresh hiring pool each quarter. Better candidates turn up as the company grows. */
export function refreshCandidates(state: GameState, co: Company, r: Rng): void {
  const year = yearOf(state.week);
  const avail = roles.filter((x) => x.year <= year);
  const n = co.level === "garage" ? 3 : co.level === "office" ? 4 : 5;
  const levelTier = { garage: 0, office: 1, studio: 2, hq: 3 }[co.level];
  const repTier = co.reputation / 40;
  const bonus = co.staff.some((s) => hasTrait(s, "networker")) ? 0.5 : 0;
  co.candidates = [];
  for (let i = 0; i < n; i++) {
    const role = i === 0 && co.level === "garage" ? r.pick(["artist", "designer", "writer"]) : r.pick(avail).id;
    const tier = clamp(Math.round(r.normal(levelTier + repTier * 0.5 + bonus + perkFx(co, "hiring") * 2, 0.9)), 0, 5);
    const s = makeStaff(state, role, tier, r) as Candidate;
    s.ask = s.salary;
    co.candidates.push(s);
  }
}

export function hireCost(c: Candidate): number {
  return Math.round(c.ask * 4); // a month's pay up front as signing
}

/** How many people your buildings hold: the main studio plus every annex. */
export function levelCap(co: Company): number {
  return C.level[co.level].staffCap + (co.annexes ?? []).reduce((s, a) => s + (C.annex[a.kind]?.staffCap ?? 0), 0);
}

/** Weekly rent before inflation: the main studio plus every annex. */
export function baseRent(co: Company): number {
  return C.level[co.level].rent + (co.annexes ?? []).reduce((s, a) => s + (C.annex[a.kind]?.rent ?? 0), 0);
}

/** How many people you can have: the building's limit, and one workstation each. */
export function staffCap(co: Company): number {
  return Math.min(levelCap(co), Math.max(1, deskCount(co)));
}

export function payroll(co: Company): number {
  return co.staff.reduce((s, x) => s + x.salary, 0);
}

export function trainingCost(s: Staff, week: number): number {
  return Math.round((300 + s.stats[C.role[s.role].main] * 6) * priceIndex(week));
}

// ------------------------------------------------------------------ rooms

export function roomFurnitureFactor(room: Room): number {
  return 0.6 + 0.4 * Math.min(1, room.furniture.length / 6);
}

/** Summed room effects (scaled by how well each room is furnished). */
export function roomFx(co: Company, key: string): number {
  // each room type's bonus counts once, from your best-furnished room of that type; more rooms add desks, not bonus
  const best = new Map<string, number>();
  for (const room of co.rooms) {
    const t = C.room[room.type];
    const x = t?.fx[key];
    if (typeof x === "number") best.set(room.type, Math.max(best.get(room.type) ?? -Infinity, x * roomFurnitureFactor(room)));
  }
  let v = 0;
  for (const x of best.values()) v += x;
  return v;
}

/**
 * Furniture effects across the whole studio: each piece adds its bit, with diminishing returns towards a cap,
 * so a well-furnished studio is clearly better without a hundred desks stacking forever.
 */
const FURN_CAP: Record<string, number> = { quality: 0.06, speed: 0.12, detect: 0.12, printQuality: 0.1, printSpeed: 0.5, fans: 0.15, directSales: 0.05, hype: 0.12, legacy: 0.5, logistics: 0.4, light: 4, petMorale: 6 };
let furnCache = new WeakMap<Company, { sig: string; fx: Record<string, number> }>();
/** Forget the summed furniture (every week, and every online quarter: see resetCaches). */
export function resetFurnitureCache(): void {
  furnCache = new WeakMap();
}

export function furnFx(co: Company, key: string): number {
  let n = 0;
  for (const r of co.rooms) n += r.furniture.length;
  const sig = `${co.rooms.length}:${n}`;
  let c = furnCache.get(co);
  if (!c || c.sig !== sig) {
    const sum: Record<string, number> = {};
    for (const r of co.rooms) for (const f of r.furniture) for (const [k, v] of Object.entries(C.furniture[f.kind]?.fx ?? {})) if (k in FURN_CAP && typeof v === "number") sum[k] = (sum[k] ?? 0) + v;
    const fx: Record<string, number> = {};
    for (const [k, v] of Object.entries(sum)) fx[k] = FURN_CAP[k] * (1 - dexp(-v / FURN_CAP[k]));
    furnCache.set(co, (c = { sig, fx }));
  }
  return c.fx[key] ?? 0;
}

/** Morale from the rooftop garden: each planter grows one plant from garden.json, which helps once it's grown. */
export function gardenMorale(co: Company, week: number): number {
  let m = 0;
  for (const r of co.rooms) if (r.type === "rooftop_garden") for (const f of r.furniture) {
    if (f.kind !== "planter") continue;
    let h = 0;
    for (const ch of f.id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
    const plant = gardenPlants[h % gardenPlants.length];
    if (plant && week - (r.builtWeek ?? 0) >= plant.weeks) m += plant.morale;
  }
  return Math.min(10, m * (roomFx(co, "planters") > 0 ? 1 : 0));
}

/** A game café's regulars show how much people really want to play each game again. */
export function cafeReadout(co: Company): boolean {
  return co.rooms.some((r) => !!C.room[r.type]?.fx["replayReadout"]);
}

export function hasRoom(co: Company, type: string): boolean {
  return co.rooms.some((r) => r.type === type);
}

export function roomCost(type: RoomType, w: number, h: number, week: number): number {
  const area = w * h;
  return Math.round(type.cost * (0.6 + area / (type.minW * type.minH) * 0.4) * priceIndex(week));
}

export function furnitureMorale(co: Company): number {
  // decor near seats counts double: each decor piece with morale counts once, plus once per nearby seat (max 2)
  let m = 0;
  for (const room of co.rooms) {
    const seats = room.furniture.filter((f) => C.furniture[f.kind]?.seat);
    for (const f of room.furniture) {
      const k = C.furniture[f.kind];
      if (!k?.fx.morale) continue;
      const rad = k.fx.radius ?? 3;
      const near = seats.filter((s) => Math.abs(s.x - f.x) + Math.abs(s.y - f.y) <= rad).length;
      m += k.fx.morale * (0.5 + 0.25 * Math.min(2, near));
    }
  }
  return Math.min(18, m * 0.6);
}

export function palletCapacity(co: Company): number {
  let cap = C.level[co.level].pallets;
  for (const room of co.rooms) {
    if (room.type === "warehouse") cap += Math.round(room.w * room.h * (C.room.warehouse.fx.palletsPerTile ?? 1.5) * roomFurnitureFactor(room));
    for (const f of room.furniture) cap += C.furniture[f.kind]?.storage ?? 0;
  }
  return Math.round(cap * (1 + perkFx(co, "palletBonus")));
}

// ------------------------------------------------------------------ work

export function workRate(co: Company, s: Staff, phase: PhaseId, crunch: boolean): { work: number; quality: number } {
  if (s.training || s.awayUntil !== undefined) return { work: 0, quality: 0 };
  const stat = phase === "prototype" && s.role === "developer" ? s.stats.systems * 0.85 : s.stats[PHASE_STAT[phase]];
  const fit = PHASE_ROLES[phase].includes(s.role) || s.founder ? 1 : 0.5;
  let speed = 1 + traitFx(s, "speed");
  if (phase === "prototype") speed += roomFx(co, "designSpeed") + perkFx(co, "designSpeed");
  speed *= 1 + perkFx(co, "speed") + furnFx(co, "speed");
  if (phase === "art") speed += roomFx(co, "artSpeed");
  if (phase === "rulebook") speed += perkFx(co, "writeSpeed");
  speed *= 0.62 + 0.85 * (s.morale / 100);
  if (crunch) speed *= 1.35 + traitFx(s, "crunch") * 0.3;
  if (s.burnout > 70) speed *= 0.6;
  const levelBoost = 1 + 0.04 * (s.level - 1);
  const work = Math.max(0, 10 * (stat / 50) * fit * speed * levelBoost);
  let quality = (stat / 100) * (0.85 + 0.15 * fit) + traitFx(s, "quality") + 0.02 * (s.level - 1);
  if (phase === "art") quality += roomFx(co, "artQuality");
  quality += furnFx(co, "quality") + perkFx(co, "quality");
  if (crunch) quality -= 0.04;
  return { work, quality: clamp(quality) };
}

// ------------------------------------------------------------------ morale, burnout, levels

export interface TeamMood {
  decor: number;
  plants: number;
  rooms: number;
  pets: number;
  teamMorale: number; // summed teamMorale traits
  peacemaker: boolean;
  breakRoom: boolean;
  /** today's price level, so "fair pay" is in the same money as salaries */
  idx: number;
  /** years since your last hit (8+), for everyone */
  yearsSinceHit: number;
  /** every desk is taken and there's nowhere to get away from it */
  crowded: boolean;
}

/** Things every person's morale shares, computed once per week. */
export function teamMood(co: Company, week = 0): TeamMood {
  return {
    decor: furnitureMorale(co),
    plants: co.rooms.reduce((n, r) => n + r.furniture.filter((f) => C.furniture[f.kind]?.plant).length, 0),
    // room bonuses, warm lamplight, and whatever the rooftop garden has grown
    rooms: roomFx(co, "morale") + furnFx(co, "light") + (co.gardenMorale ?? 0),
    pets: co.pets.length,
    teamMorale: co.staff.reduce((s, x) => s + traitFx(x, "teamMorale"), 0),
    peacemaker: co.staff.some((x) => hasTrait(x, "peacemaker")),
    breakRoom: hasRoom(co, "break_room"),
    idx: priceIndex(week),
    // only a studio that has had a hit misses having one
    yearsSinceHit: lastHitYears(co, week),
    crowded: co.staff.length >= deskCount(co) && co.staff.length > 4 && !hasRoom(co, "break_room"),
  };
}

/** What rooms, decor and pets add to morale: each capped, and all three together at most 18. */
export function comfort(co: Company, s: Staff, mood: TeamMood): number {
  const pets = Math.min(6, mood.pets * 2 + (mood.pets ? furnFx(co, "petMorale") : 0)) * (hasTrait(s, "pet_lover") ? 2 : 1);
  return Math.min(18, Math.min(12, mood.rooms) + Math.min(6, mood.decor) + pets);
}

/** The pull-down on very experienced people: half a point per level above 10, at most 5. */
export function veteran(s: Staff): number {
  return s.founder ? 0 : Math.min(5, Math.max(0, s.level - 10) * 0.5);
}

export function moraleTarget(co: Company, s: Staff, recentHits: number, mood: TeamMood = teamMood(co)): number {
  let t = 58;
  const fair = fairPay(s, mood.idx);
  if (!s.founder) t += clamp((s.salary / Math.max(1, fair) - 1) * 40, -15, 10);
  // nice rooms, decor and pets help, up to a point
  t += comfort(co, s, mood);
  // veterans have seen it all: past level 10 it takes hits and good pay to keep them happy
  t -= veteran(s);
  if (hasTrait(s, "plant_parent")) t += Math.min(8, mood.plants * 2);
  t += Math.min(10, perkFx(co, "morale")) + perkFx(co, "plantMorale") * Math.min(1, mood.plants / 3);
  if (hasTrait(s, "homesick") && !mood.breakRoom) t -= 10;
  t += Math.min(8, recentHits * 3);
  // pressure: nothing to do, a long time without a hit, no room to breathe
  if ((s.idleWeeks ?? 0) > 6) t -= Math.min(8, (s.idleWeeks ?? 0) - 6);
  if (mood.yearsSinceHit > 3) t -= Math.min(8, (mood.yearsSinceHit - 3) * 2);
  if (mood.crowded) t -= 5;
  // lost in a huge team
  if (s.projectId) {
    const team = co.staff.filter((x) => x.projectId === s.projectId).length;
    if (team > 12) t -= Math.min(6, (team - 12) * 0.5);
  }
  t += traitFx(s, "morale");
  // the people who lift a room: a few help, a crowd of them doesn't add up for ever
  t += Math.max(-6, Math.min(6, (mood.teamMorale - traitFx(s, "teamMorale")) * 0.5));
  if (!mood.peacemaker) {
    const clashes = s.traits.flatMap((a) => C.trait[a]?.clash ?? []);
    if (clashes.length) for (const other of co.staff) if (other.id !== s.id && other.traits.some((b) => clashes.includes(b))) t -= 6;
  }
  if (s.crunch) t -= 14;
  if (co.cash < 0) t -= 8;
  return clamp(t, 5, 100);
}

export const XP_PER_LEVEL = 3;

/** What a person of this skill is worth in today's money. */
export function fairPay(s: Staff, idx: number): number {
  const role = C.role[s.role];
  return role.salary * (0.55 + s.stats[role.main] / 70) * idx;
}

export const MAX_LEVEL = 20;

/** Experience for the next level: each level a little harder than the last. */
export function xpNeeded(level: number): number {
  return Math.round(XP_PER_LEVEL * level * (1 + level / 3.5));
}

/**
 * How quickly someone learns: a hidden talent from 0.6× to 1.5× (the founder learns a little faster than average).
 * It's why one hire is level 14 by the 2000s and another, hired the same week, is level 8.
 */
export function talent(s: Staff): number {
  if (s.founder) return 1.1;
  return 0.6 + (hashString(`${s.id}:${s.name}:talent`) % 1000) / 1000 * 0.9;
}

/** How the talent shows on a staff card, once they've been around a while. */
export function talentLabel(s: Staff): string | null {
  const t = talent(s);
  return t >= 1.25 ? "Learns fast" : t <= 0.78 ? "Slow to learn" : null;
}

export function grantXp(s: Staff, amount: number): boolean {
  s.xp += amount * (1 + traitFx(s, "xp"));
  if (s.xp >= xpNeeded(s.level) && s.level < MAX_LEVEL) {
    s.xp = 0;
    s.level += 1;
    for (const k of STATS) {
      const v = s.stats[k];
      const main = k === C.role[s.role].main;
      // fast while learning, slow once good: the last points to 99 take a career
      const gain = main ? (v < 75 ? 4 : v < 88 ? 2 : v < 96 ? 1 : 0.5) : v < 55 ? 1 : v < 75 ? 0.5 : 0;
      s.stats[k] = Math.min(99, Math.round((v + gain) * 2) / 2);
    }
    if (!s.founder) s.salary = Math.round(s.salary * 1.06);
    return true;
  }
  return false;
}

/** Years since the studio's last hit (8+); 0 if it has never had one. */
function lastHitYears(co: Company, week: number): number {
  let last = -1;
  for (const p of co.products) if (p.launched && p.score >= 8 && p.releaseWeek > last) last = p.releaseWeek;
  return last < 0 ? 0 : (week - last) / 52;
}
